import Box from '@mui/material/Box'
import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import FormControlLabel from '@mui/material/FormControlLabel'
import Radio from '@mui/material/Radio'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import { useTranslate } from '@repo/unicity'
import BankwireIcon from '../../assets/icons/svg/BankwireIcon.svg'
import CreditCardIcon from '../../assets/icons/svg/CreditCardIcon.svg'
import RadioIconUnchecked from '../../assets/icons/svg/RadioIconUnchecked.svg'
import RadioIconChecked from '../../assets/icons/svg/RadioIconChecked.svg'
import CreditCardBox from './CreditCardBox'
import { useAtom, useAtomValue } from 'jotai'
import { creditCardState } from '../store/creditCard'
import { useNavigate } from 'react-router'
import { useEffect, useMemo, useState } from 'react'
import { useQueryParams } from '../../hooks/useQueryParams'
import { paymentApis, type TUpdateLog } from './Apis'
import type { TOrder } from '../../types/TOrderCalc'
import {
    authenticationState,
    cartState,
    countryState,
    customerState,
    languageState,
    periodState,
    productsState,
    totalPriceInCart,
    type TProductCredit,
    useLocale
} from '@repo/unicity'
import type { TTerms } from '../../types/TTerms'
import { get } from 'lodash'
import ErrorBox from '../errorBox/ErrorBox'
import Bowser from 'bowser'
import WorldPay3DS from './WorldPay3DS'
import type { THydraOrder } from '../../types/THydraOrder'
import { NumberHelpers } from '@repo/unicity'
import { spotlightCustomerHelpers as customerHelpers } from '../../helpers/customerCompat'
import BankwireBox from './BankwireBox'
import { CreditCardHelper } from './CreditCardHelper'
import LoadingModal from '../loadingModal/LoadingModal'
import { isAllowedAddressHistoryState, paymentProcessState } from '../store/loadingState'
import { addressHistoryState } from '../core/useAddressHistory.ts/addressHistoryState'
import { orderDetailState } from '../store/orderDetail'
import { addressApis } from '../address/Apis'
import SlideToPay from '../slideToDo/SlideToPay'
import { orderDetailsApis } from '../order-details/Apis'
import { TOrderDataItems } from '../../types/TValidateOrderLimit'

const Payment = ({ productCredits }: { productCredits: TProductCredit | null }) => {
    const cart = useAtomValue(cartState)

    const country = useAtomValue(countryState)
    const { locale } = useLocale()
    const [selectedPayment, setSelectedPayment] = useState<'bankwire' | 'credit_card' | ''>('credit_card')
    const { translate } = useTranslate()
    const qs = useQueryParams()
    const auth = useAtomValue(authenticationState)
    const navigate = useNavigate()
    const creditCard = useAtomValue(creditCardState)
    const customer = useAtomValue(customerState)
    const [loading, setLoading] = useState(false)
    const [, setTerms] = useState<TTerms[]>([])
    const [error, setError] = useState<string[]>([])
    const [creditCardError, setCreditCardError] = useState<string[]>([])
    const [worldPay, setWorldPay] = useState<{ ddcHTML: string; sessionId: string; challengeHTML: string }>({
        ddcHTML: '',
        sessionId: '',
        challengeHTML: ''
    })
    const [hydraCreateOrderRequestBody, setHydraCreateOrderRequestBody] = useState<string>('')
    const [hydraCreateOrderResponseBody, setHydraCreateOrderResponseBody] = useState<string>('')
    const [hydraCreateOrderResponseHeader, setHydraCreateOrderResponseHeader] = useState<string>('')
    const [paymentId, setPaymentId] = useState<string>('')
    const [uuid] = useState<string>(qs.get('uuid') || '')
    const language = useAtomValue(languageState)
    const [openProcessingModal, setOpenProcessingModal] = useState(false)
    const [processStates, setProcessStates] = useAtom(paymentProcessState)
    const [isFailed, setIsFailed] = useState(false)

    const totalPrice = useAtomValue(totalPriceInCart)
    const [isFullProductCredit, setIsFullProductCredit] = useState(false)

    const products = useAtomValue(productsState)
    const period = useAtomValue(periodState)
    const addressHistory = useAtomValue(addressHistoryState)
    const isAllowedAddressHistory = useAtomValue(isAllowedAddressHistoryState)

    const orderDetail = useAtomValue(orderDetailState)

    const isEqualQty = cart.every(cartItem => {
        const itemFormFormat = orderDetail.ushop.productsDisplay.find(p => p.item_code === cartItem.product.item_code)
        return itemFormFormat && cartItem.quantity === itemFormFormat.qty
    })

    const toCurl = ({
        method = `POST`,
        url,
        headers = {
            'Content-Type': `application/json`
        },
        dataRaw
    }: {
        method?: string
        url: string
        headers?: Record<string, string>
        dataRaw: string
    }) => {
        const curlLines = [
            `curl --location --request ${method} '${url}'`,
            ...Object.keys(headers).map(key => `--header '${key}: ${headers[key]}'`),
            `--data-raw '${dataRaw}'`
        ]

        return curlLines.join(` `)
    }

    type IUuidItemCart = {
        item_code: string
        qty: number
    }
    type TShipToName = {
        [key: string]: string
    }
    const handlePayment = async () => {
        let uuidUpdated = uuid
        let failedOrderLog: TUpdateLog | null = null
        try {
            setError([])
            setCreditCardError([])
            setIsFailed(false)
            setLoading(true)

            const expDate = creditCard.expDate.split('-')
            expDate[0] = (Number(expDate[0]) - 2000).toString()

            if (selectedPayment === 'credit_card') {
                const creditCardValidated = CreditCardHelper.cardValidate({
                    countryCode: country.shorter,
                    creditCard: {
                        creditCardNumber: creditCard.creditCardNumber.replace(/\s/g, ''),
                        payer: creditCard.payer,
                        creditCardExpires: `${expDate[1]}/${expDate[0]}`,
                        creditCardSecurityCode: creditCard.cvv
                    }
                })

                if (creditCardValidated.success === false) {
                    setCreditCardError(creditCardValidated.error_messages)
                    return
                }
            }
            if (!uuid || !isEqualQty) {
                const items: IUuidItemCart[] = []
                products.forEach(product => {
                    items.push({
                        item_code: product.item_code,
                        qty: (cart.find(c => c.product.item_code === product.item_code)?.quantity as number) || 1
                    })
                })

                let address = {
                    shipToAddress: {
                        address1: '',
                        address2: '',
                        city: '',
                        state: '',
                        zip: '',
                        country: ''
                    },
                    shipToName: {
                        firstName: '',
                        lastName: '',
                        fullName: ''
                    } as TShipToName
                }
                address.shipToAddress = {
                    address1: customer.mainAddress.address1,
                    address2: customer.mainAddress.address2,
                    city: customer.mainAddress.city,
                    state: customer.mainAddress.state,
                    zip: customer.mainAddress.zip,
                    country: customer.mainAddress.country
                }
                address.shipToName = customer.humanName
                // if (selectedAddressHistory.addressHref === 'main' || selectedAddressHistory.addressHref.length === 0) {
                //     address = addressHistory.mainAddress
                // } else {
                //     const item = addressHistory.items.find(item => item.addressHref === selectedAddressHistory.addressHref)
                //     if (item) {
                //         address = item
                //     }
                // }
                if (isAllowedAddressHistory) {
                    if (!addressHistory.mainAddress) {
                        if (addressHistory.items.length > 0 && addressHistory.items[0]) {
                            address = addressHistory.items[0]
                        }
                    } else {
                        address = addressHistory.mainAddress
                    }
                }

                const fullName = (address.shipToName['fullName@ja'] as string) || address.shipToName.fullName || ''

                const zip = address.shipToAddress.zip
                const shipToAddress = address.shipToAddress
                const email = orderDetail.ushop.detail_page.email.length > 0 ? orderDetail.ushop.detail_page.email : customer?.email
                const phone = orderDetail.ushop.detail_page.phone.length > 0 ? orderDetail.ushop.detail_page.phone : customerHelpers.customerPhone(customer)
                const postData = {
                    fullname_jp: fullName,
                    token: auth.token as string,
                    ba_status: customerHelpers.customerStatus(customer) as string,
                    email: email,
                    login_id: customer?.id.unicity,
                    login_name: customer?.humanName.fullName,
                    login_name_native: customerHelpers.customerNameNative(customer) || '',
                    period: period.entryPeriod,
                    phone: phone.replace(/-/g, ''),
                    agent: 'UNISHOP-WEB-JP',
                    medium: 'Internet',
                    platform: 'platform',
                    send_confirmation: true,
                    shipment_options: 'pickuptokyo',
                    shipToAddress: shipToAddress,
                    items: JSON.stringify(items),
                    district: '',
                    prefecture: '',
                    town: '',
                    house_number: '',
                    zip: zip,
                    selectAddressHistory: 'addressHistory'
                }

                const response = await addressApis.createUuid({ data: postData })
                if (response.data.success) {
                    const url = new URL(window.location.href)
                    url.searchParams.set('uuid', response.data.uuid)
                    window.history.replaceState({}, '', url.toString())
                    uuidUpdated = response.data.uuid
                } else {
                    setCreditCardError(response.data.Message_v2)
                    return
                }
            }

            setOpenProcessingModal(true)
            setProcessStates({
                orderProcessing: 1,
                paymentProcessing: 1,
                logProcessing: 1
            })
            const items = [] as {
                item: {
                    href: string
                }
                quantity: number
            }[]
            const itemsUpdateToUuid = [] as {
                item_code: string
                qty: number
            }[]
            cart.forEach(item => {
                items.push({
                    item: {
                        href: `https://hydra.unicity.net/v5a/items?id.unicity=${item.product.item_code}`
                    },
                    quantity: item.quantity
                })
                itemsUpdateToUuid.push({
                    item_code: item.product.item_code,
                    qty: item.quantity
                })
            })

            const browserInfo = Bowser.parse(window.navigator.userAgent)
            const platform = browserInfo.os.name || 'web'
            const order: TOrder = {
                source: {
                    agent: 'spotlight',
                    medium: 'Template',
                    platform: platform || 'web'
                },
                customer: {
                    href: customer.href
                },
                market: country.shorter,
                shipToAddress: {
                    country: country.shorter
                },
                shippingMethod: {
                    href: 'https://hydra.unicity.net/v5a/shippingmethods?type=WillCall&location=Japan, Tokyo Warehouse'
                },
                lines: {
                    items: items
                }
            }
            // let uuidUpdated = uuid
            // const newOrderDetail = JSON.parse(JSON.stringify(orderDetail)) as TOrderDetail
            // const detailPage = newOrderDetail.ushop.detail_page

            // if (orderDetail.ushop.detail_page.items.length !== itemsUpdateToUuid.length) {
            //     const postData = {
            //         fullname_jp: detailPage.fullname_jp,
            //         token: auth.token as string,
            //         ba_status: customerHelpers.customerStatus(customer) as string,
            //         email: detailPage.email.length > 0 ? detailPage.email : customer?.email,
            //         login_id: customer?.id.unicity,
            //         login_name: customer?.humanName.fullName,
            //         login_name_native: customerHelpers.customerNameNative(customer) || '',
            //         period: detailPage.period,
            //         phone: detailPage.phone.length > 0 ? detailPage.phone : customerHelpers.customerPhone(customer),
            //         agent: 'UNISHOP-WEB-JP',
            //         medium: 'Internet',
            //         platform: 'platform',
            //         send_confirmation: true,
            //         shipment_options: 'pickuptokyo',
            //         shipToAddress: JSON.stringify(detailPage.shipToAddress),
            //         items: JSON.stringify(itemsUpdateToUuid),
            //         district: detailPage.district,
            //         prefecture: detailPage.prefecture,
            //         town: detailPage.town,
            //         house_number: detailPage.house_number,
            //         zip: detailPage.zip
            //     }

            //     const newUuidResponse = await addressApis.createUuid({
            //         data: postData
            //     })
            //     if (newUuidResponse.status === 200) {
            //         const newUuid = newUuidResponse.data.uuid
            //         const url = new URL(window.location.href)
            //         url.searchParams.set('uuid', newUuid)
            //         window.history.replaceState({}, '', url.toString())
            //         uuidUpdated = newUuid
            //         setUuid(newUuid)
            //     }
            // }

            const responseCreatePayment = await paymentApis.orderCal({ order, countryCode3Alpha: country.short })
            const responseTerm = get(responseCreatePayment.data, 'items[0].terms', {} as TTerms)
            setTerms(responseTerm)

            const validateOrderLimitResponse = await validateOrderLimit(responseCreatePayment.data.items[0])
            if (validateOrderLimitResponse === false) {
                return
            }

            // sourpath remove query params
            const sourcePath = window.location.href.split('?')[0] ?? ''
            const response = await paymentApis.createPayment({ uuid: uuidUpdated, sourcePath: sourcePath, countryCode3Alpha: country.short })
            setProcessStates(prev => ({ ...prev, orderProcessing: 2 }))
            const paymentId = get(response.data, 'payment_id', '')
            setPaymentId(paymentId)
            const totalAmount = get(response.data, 'ushop.total_price', 0)
            const totalAvailableBalance = productCredits ? productCredits.availableBalance : 0
            // const totalAvailableBalance = 0
            const canPayAllWithProductCredit = totalAvailableBalance - totalAmount >= 0
            // const canPayAllWithProductCredit = false
            const hydra = structuredClone(response.data.hydra) as THydraOrder
            let isPaywithWorldPay3DS = false
            let challengeHTML = ''
            let ddcHTML = ''
            let sessionId = ''

            if (canPayAllWithProductCredit) {
                hydra.transactions = {
                    items: [
                        {
                            amount: totalAmount,
                            type: 'AuthorizeAndCapture',
                            method: 'StoreCredit'
                        }
                    ]
                }
            } else {
                if (selectedPayment === 'credit_card') {
                    const restAmount = totalAmount - totalAvailableBalance
                    // const restAmount = totalAmount
                    if (restAmount > 0) {
                        const creditCardNumber = creditCard.creditCardNumber.replace(/\s/g, '')
                        const responseDDC = await paymentApis.requestWorldPayDDC({ cardNumber: creditCardNumber, countryCode3Alpha: country.short })
                        if (responseDDC) {
                            isPaywithWorldPay3DS = true
                            ddcHTML = responseDDC.data.ddcHTML
                            sessionId = responseDDC.data.sessionId
                            const transactionsItems = []
                            if (productCredits && totalAvailableBalance > 0) {
                                transactionsItems.push({
                                    amount: productCredits.availableBalance,
                                    type: 'AuthorizeAndCapture',
                                    method: 'StoreCredit'
                                })
                            }
                            if (restAmount > 0) {
                                transactionsItems.push({
                                    amount: restAmount,
                                    type: 'IOU',
                                    method: 'CreditCard',
                                    methodDetails: {
                                        payer: creditCard.payer,
                                        creditCardExpires: creditCard.expDate.split('/').reverse().join('/'),
                                        creditCardNumber: creditCardNumber,
                                        creditCardSecurityCode: creditCard.cvv,
                                        languageDialect: locale,
                                        challengeWindowSize: '390x400',
                                        sessionId: sessionId,
                                        bankName: 'WorldPay',
                                        billToAddress: {
                                            country: country.shorter
                                        }
                                    }
                                })
                            }
                            hydra.transactions = {
                                items: transactionsItems
                            }
                        }
                    }
                } else {
                    hydra.transactions = {
                        items: [
                            {
                                amount: totalAmount,
                                type: 'IOU',
                                method: 'BankWire'
                            }
                        ]
                    }
                }
            }
            failedOrderLog = {
                payments_id: paymentId,
                payment_status: 'failed',
                order_number: '',
                approval_code: '',
                dist_id: customer.id.unicity,
                dist_status: customerHelpers.customerStatus(customer) || '',
                hydra_curl: toCurl({
                    method: 'POST',
                    url: `https://hydra.unicity.net/v5a/customers/me/orders`,
                    headers: { Authorization: `${auth.token}` },
                    dataRaw: JSON.stringify(hydra)
                }),
                hydra_response_header: '',
                hydra_response_body: ''
            }
            const responseCreateOrder = await paymentApis.createOrder({
                data: hydra,
                token: auth.token as string,
                countryCode3Alpha: country.short
            })
            if (isPaywithWorldPay3DS) {
                const getChallengeHtml = responseCreateOrder.data.transactions.items.find(
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    (item: any) => item.method === 'CreditCard' && item.methodDetails.challengeHTML !== ''
                )
                if (getChallengeHtml) {
                    const methodDetails = getChallengeHtml.methodDetails
                    challengeHTML = methodDetails.challengeHTML || ''
                }
                setWorldPay({
                    ddcHTML: ddcHTML,
                    sessionId: sessionId,
                    challengeHTML: challengeHTML
                })
                setHydraCreateOrderRequestBody(JSON.stringify(hydra))
                setHydraCreateOrderResponseBody(JSON.stringify(responseCreateOrder.data))
                setHydraCreateOrderResponseHeader(JSON.stringify(responseCreateOrder.headers))
            } else {
                setProcessStates(prev => ({ ...prev, paymentProcessing: 2 }))
                const logOrder: TUpdateLog = {
                    ...failedOrderLog,
                    payment_status: 'success',
                    order_number: responseCreateOrder.data.id.unicity,
                    hydra_response_header: JSON.stringify(responseCreateOrder.headers),
                    hydra_response_body: JSON.stringify(responseCreateOrder.data)
                }

                await Promise.allSettled([paymentApis.updateLog({ data: logOrder })])
                setProcessStates(prev => ({ ...prev, logProcessing: 2 }))
                navigate(`/payment-response/success?paymentId=${paymentId}`)
            }
        } catch (error) {
            console.error(error, get(error, 'response.data.error.error.errors', ''))
            if (failedOrderLog) {
                void Promise.allSettled([
                    paymentApis.updateLog({
                        data: {
                            ...failedOrderLog,
                            hydra_response_header: JSON.stringify(get(error, 'response.headers', {})),
                            hydra_response_body: JSON.stringify(get(error, 'response.data', {}))
                        }
                    })
                ])
            }

            if (['6002', '1603'].includes(get(error, 'response.data.error.error_code', '0').toString())) {
                setError(['order_quantity_limits_2qty_in_1_day', `UUID: ${uuidUpdated}`])
            } else if (get(error, 'response.data.error.error.errors', []).length > 0) {
                setError([...get(error, 'response.data.error.error.errors', []), `UUID: ${uuidUpdated}`])
            } else if (get(error, 'response.data.error.code', '0').toString() === '500') {
                setError(['internal_server_error', `UUID: ${uuidUpdated}`])
            } else if (get(error, 'response.data.error.success', false)) {
                setError(['duplicated_order', `UUID: ${uuidUpdated}`])
            } else if (['3009'].includes(get(error, 'response.data.error.error_code', '0').toString())) {
                setError([get(error, 'response.data.error.error_message', ''), `UUID: ${uuidUpdated}`])
            }
            setIsFailed(true)
            setLoading(false)
        } finally {
            setLoading(false)
        }
    }

    useEffect(() => {
        if (isFailed) {
            setProcessStates({
                orderProcessing: processStates.orderProcessing === 1 ? -1 : processStates.orderProcessing,
                paymentProcessing: processStates.paymentProcessing === 1 ? -1 : processStates.paymentProcessing,
                logProcessing: processStates.logProcessing === 1 ? -1 : processStates.logProcessing
            })
        }
    }, [isFailed, processStates.logProcessing, processStates.orderProcessing, processStates.paymentProcessing])

    useEffect(() => {
        if (productCredits && cart.length > 0) {
            const productTotal = cart.reduce((acc, item) => acc + item.product.price * item.quantity, 0)
            if (productCredits.availableBalance >= productTotal) {
                setIsFullProductCredit(true)
                setSelectedPayment('')
            } else {
                setIsFullProductCredit(false)
                setSelectedPayment('credit_card')
            }
        }
    }, [productCredits, cart])

    const CreditCardIconsRow = useMemo(() => {
        return (
            <Stack direction={'row'} gap={'8px'}>
                {country.payment_options.map(option => {
                    return <img src={CreditCardHelper.BrandOfCard(option)} alt={option} style={{ height: '16px' }} />
                })}
            </Stack>
        )
    }, [country.payment_options])

    // Memoize cart total to avoid recalculating
    const cartTotal = useMemo(() => cart.reduce((acc, item) => acc + item.product.price * item.quantity, 0), [cart])

    // Memoize product credit calculation
    const productCreditAmount = useMemo(() => {
        if (productCredits && productCredits.availableBalance > 0) {
            return productCredits.availableBalance >= cartTotal ? cartTotal : productCredits.availableBalance
        }
        return 0
    }, [productCredits, cartTotal])

    // Memoize ready to pay state
    const isReadyToPay = useMemo(() => {
        if (isFullProductCredit) return true
        if (selectedPayment === 'bankwire') return true
        return creditCard.creditCardNumber.length >= 16 && creditCard.expDate.length > 0 && creditCard.cvv.length > 0 && creditCard.payer.length > 0
    }, [isFullProductCredit, selectedPayment, creditCard])

    const validateOrderLimit = async (orderData: TOrderDataItems) => {
        try {
            const orderDataItems = orderData.lines.items.map(item => {
                return {
                    item_code: item.item.id.unicity,
                    quantity: item.quantity,
                    price: item.terms.priceEach,
                    pv: item.terms.pv
                }
            })

            const responseValidateOrderLimit = await orderDetailsApis.validateOrderLimit({
                baId: customer.id.unicity,
                countryCode3Alpha: country.short,
                period: orderData.terms.period,
                totalPrice: orderData.terms.total,
                totalPv: orderData.terms.pv,
                subtotalPrice: orderData.terms.subtotal,
                orderDataItems: orderDataItems
            })

            if (responseValidateOrderLimit.data.success === false) {
                const errors = responseValidateOrderLimit.data.errors ?? []
                errors.forEach((error: { translation: { english: string; native: string } }) => {
                    const errorMessage = language === 'EN' ? error.translation.english : error.translation.native
                    setCreditCardError([errorMessage])
                })

                setIsFailed(true)
                setLoading(false)
                setOpenProcessingModal(false)
                return false
            }
        } catch (error) {
            console.error(error)
            setIsFailed(true)
            setLoading(false)
            setOpenProcessingModal(false)
            setCreditCardError(['API validate order error'])
            throw new Error('API validate order limit failed')
        }
    }

    return (
        <Card
            sx={{
                padding: {
                    xs: '16px',
                    md: '32px'
                }
            }}>
            <CardContent>
                <Stack
                    direction={'column'}
                    gap={{
                        xs: '16px',
                        sm: '24px'
                    }}>
                    <Typography variant="cardTitle" color={'#003B6F'}>
                        {translate('payment')}
                    </Typography>
                    {!isFullProductCredit && (
                        <Typography variant="cardSubtitle" color={'#003B6F'}>
                            {translate('payment_subtitle')}
                        </Typography>
                    )}
                </Stack>
                <Stack
                    direction={'column'}
                    gap={'16px'}
                    mt={{
                        xs: '24px',
                        sm: '32px'
                    }}
                    position={'relative'}>
                    {selectedPayment === 'bankwire' ? (
                        <BankwireBox RowItem={PaymentItem} isChecked={selectedPayment === 'bankwire'} onClick={() => setSelectedPayment('bankwire')} />
                    ) : productCredits && productCredits?.availableBalance > 0 ? null : (
                        <PaymentItem
                            item={{
                                name: translate('bankwire'),
                                key: 'bankwire',
                                icon: <img src={BankwireIcon} alt="BankwireIcon" />
                            }}
                            isChecked={false}
                            onClick={() => setSelectedPayment('bankwire')}
                            disabled={isFullProductCredit}
                        />
                    )}

                    {selectedPayment === 'credit_card' ? (
                        <CreditCardBox
                            RowItem={PaymentItem}
                            isChecked={selectedPayment === 'credit_card'}
                            onClick={() => setSelectedPayment('credit_card')}
                            isLoading={loading}
                        />
                    ) : isFullProductCredit ? null : (
                        <PaymentItem
                            item={{
                                name: translate('credit_card'),
                                key: 'credit_card',
                                subName: CreditCardIconsRow,
                                icon: <img src={CreditCardIcon} alt="CreditCardIcon" />
                            }}
                            itemNameWrapperSx={{
                                flexDirection: 'row',
                                justifyContent: 'space-between',
                                width: '100%'
                            }}
                            isChecked={false}
                            onClick={() => setSelectedPayment('credit_card')}
                            disabled={isFullProductCredit}
                        />
                    )}
                </Stack>
                {creditCardError.length > 0 && (
                    <Stack mt="24px">
                        <ErrorBox messages={creditCardError} />
                    </Stack>
                )}
                <Stack
                    padding={'17px 16px'}
                    bgcolor={'rgba(255, 241, 220, 1)'}
                    direction={'row'}
                    justifyContent={'space-between'}
                    alignItems={'center'}
                    sx={{
                        borderRadius: '8px',
                        '@media screen and (max-width: 768px)': {
                            display: 'flex!important',
                            marginTop: productCredits && productCredits?.availableBalance > 0 && isFullProductCredit ? '0px' : '20px'
                        },
                        '@media screen and (min-width: 769px)': {
                            marginTop: productCredits && productCredits?.availableBalance > 0 && isFullProductCredit ? '0px' : '24px'
                        }
                    }}>
                    <Typography variant="cardTitle" color={'#003B6F'} fontSize={'14px'}>
                        {translate('total_payment')}
                    </Typography>
                    <Typography variant="cardTitle" color={'#003B6F'} fontSize={'14px'}>
                        {NumberHelpers.formatNumber(totalPrice - productCreditAmount)} {country?.config.currency.code}
                    </Typography>
                </Stack>

                <Stack
                    direction={'row'}
                    justifyContent={'space-between'}
                    alignItems={'center'}
                    columnGap={'16px'}
                    rowGap={'16px'}
                    sx={{
                        '@media screen and (max-width: 768px)': {
                            display: 'flex!important',
                            marginTop: '20px'
                        },
                        '@media screen and (min-width: 769px)': {
                            marginTop: '24px'
                        }
                    }}>
                    <SlideToPay
                        onSlideComplete={() => handlePayment()}
                        reset={isFailed || creditCardError.length > 0 || error.length > 0}
                        disabled={!isReadyToPay}
                    />
                    <Box
                        onClick={() => {
                            window.location.href = `https://ushop.unicity.com/japan?lang=${language}`
                        }}
                        sx={{ cursor: 'pointer' }}>
                        <Typography variant="link" color={'#003B6F'}>
                            {translate('cancel')}
                        </Typography>
                    </Box>
                </Stack>
                {worldPay && paymentId && hydraCreateOrderRequestBody && hydraCreateOrderResponseBody && hydraCreateOrderResponseHeader && (
                    <WorldPay3DS
                        ddcHTML={worldPay.ddcHTML}
                        challengeHTML={worldPay.challengeHTML}
                        hydraCreateOrderRequestBody={hydraCreateOrderRequestBody}
                        hydraCreateOrderResponseBody={hydraCreateOrderResponseBody}
                        hydraCreateOrderResponseHeader={hydraCreateOrderResponseHeader}
                        paymentId={paymentId}
                        reset={() => setWorldPay({ ddcHTML: '', sessionId: '', challengeHTML: '' })}
                        setError={setError}
                    />
                )}
                <LoadingModal
                    error={error}
                    open={openProcessingModal}
                    onClose={() => setOpenProcessingModal(false)}
                    isFailed={isFailed}
                    purchaseDetailDictionaryKey={
                        selectedPayment === 'credit_card' ? 'processing_your_purchase_detail' : 'processing_your_product_noOtp_purchase_detail'
                    }
                />
            </CardContent>
        </Card>
    )
}
type TPaymentItem = {
    item: {
        name: string
        key: string
        icon: React.ReactNode
        subName?: React.ReactNode
    }
    isChecked: boolean
    children?: React.ReactNode
    sx?: React.CSSProperties
    onClick?: () => void
    itemNameWrapperSx?: React.CSSProperties
    disabled?: boolean
}
const PaymentItem = ({ item, isChecked, children, sx, onClick, itemNameWrapperSx, disabled }: TPaymentItem) => {
    return (
        <Stack
            direction={'column'}
            gap={'16px'}
            key={item.name}
            sx={{
                backgroundColor: isChecked ? 'rgba(244, 248, 251, 1)' : 'transparent',
                border: isChecked ? '2px solid #99BCDF' : '1px solid #CCDEEF',
                borderRadius: '8px',
                padding: {
                    xs: '15px 16px'
                },
                justifyContent: 'space-between',
                flexDirection: 'row',
                alignItems: 'center',
                ...sx
            }}>
            <Stack
                sx={{
                    width: '100%'
                }}>
                <FormControlLabel
                    control={
                        <Radio
                            disableTouchRipple
                            disableFocusRipple
                            disableRipple
                            disabled={disabled}
                            checked={isChecked && !disabled}
                            onChange={onClick}
                            icon={<img src={RadioIconUnchecked} alt="RadioIconUnchecked" />}
                            checkedIcon={<img src={RadioIconChecked} alt="RadioIconChecked" />}
                            sx={{ paddingY: '0px', paddingRight: '24px', paddingLeft: '12px' }}
                        />
                    }
                    label={
                        <Stack direction={'row'} gap={'16px'} alignItems={'flex-start'}>
                            <Stack direction={'row'} gap={'16px'}>
                                {item.icon}
                            </Stack>
                            <Stack gap={'8px'} pt="3px" sx={itemNameWrapperSx}>
                                <Typography
                                    variant="body1"
                                    fontSize={{
                                        xs: '14px',
                                        sm: '16px'
                                    }}
                                    fontWeight={isChecked && !disabled ? '700' : '400'}
                                    fontFamily={'Inter, NotoSansThai'}
                                    color={isChecked && !disabled ? '#003B6F' : '#5A8FC3'}>
                                    {item.name}
                                </Typography>
                                {item.subName && item.subName}
                            </Stack>
                        </Stack>
                    }
                    name={item.name}
                    sx={{
                        alignItems: 'flex-start',
                        marginRight: '0px',
                        '& .MuiRadio-root': {
                            paddingTop: '3px'
                        },
                        '& .MuiFormControlLabel-label': {
                            width: '100%'
                        },
                        '& .MuiButtonBase-root': {
                            paddingLeft: '15px',
                            paddingRight: {
                                xs: '16px',
                                sm: '24px'
                            }
                        }
                    }}
                />
                {children && children}
            </Stack>
        </Stack>
    )
}

export default Payment
